
import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { LearnModule, ModuleFormData } from '@/types/module'

interface ModuleFormDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (formData: ModuleFormData) => Promise<boolean>
  editingModule?: LearnModule | null
  title: string
  description: string
}

const ModuleFormDialog = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  editingModule, 
  title, 
  description 
}: ModuleFormDialogProps) => {
  const [formData, setFormData] = useState<ModuleFormData>({ 
    title: '', 
    description: '', 
    category: 'Wealth',
    coins: '100',
    image_url: '',
    order_index: '1'
  })

  const categories = ['Wealth', 'Self', 'Social']

  useEffect(() => {
    if (editingModule) {
      setFormData({ 
        title: editingModule.title, 
        description: editingModule.description, 
        category: editingModule.category,
        coins: editingModule.coins.toString(),
        image_url: editingModule.image_url || '',
        order_index: editingModule.order_index.toString()
      })
    } else {
      setFormData({ 
        title: '', 
        description: '', 
        category: 'Wealth',
        coins: '100',
        image_url: '',
        order_index: '1'
      })
    }
  }, [editingModule, isOpen])

  const handleSubmit = async () => {
    const success = await onSubmit(formData)
    if (success) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="title">Module Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Enter module title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Enter module description"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="coins">Coins Reward</Label>
              <Input
                id="coins"
                type="number"
                value={formData.coins}
                onChange={(e) => setFormData({...formData, coins: e.target.value})}
                placeholder="100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="order">Order</Label>
              <Input
                id="order"
                type="number"
                value={formData.order_index}
                onChange={(e) => setFormData({...formData, order_index: e.target.value})}
                placeholder="1"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="image_url">Image URL (optional)</Label>
            <Input
              id="image_url"
              value={formData.image_url}
              onChange={(e) => setFormData({...formData, image_url: e.target.value})}
              placeholder="https://example.com/image.jpg"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {editingModule ? 'Update' : 'Create'} Module
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ModuleFormDialog
